import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import {
    setLeaderboardSortBy,
    fetchAll,
    SORT_MAP,
    leaderboardFetchFiltered,
    setSelectedListKey,
    setSelectedListIndex,
    setLeaderboardSortDirection,
} from '../../data/redux/slices/leaderboardSlice';
import './LeaderBoard.css';
import { resetPreventNextLoadMore, setAppRoute } from '../../data/redux/slices/appSlice';
import { Helmet } from 'react-helmet';
import { SubmitForm } from '../submitform/SubmitForm';
import { ResultsListing } from '../resultsListing/ResultsListing';
import { resultsCellRenderers } from '../resultsListing/renderers/ResultsCellRenderers';
import { resultsColumns } from '../resultsListing/columns/ResultsColumns';
import { resultsColumnRenderers } from '../resultsListing/renderers/ResultsColumnRenderers';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { BreadCrumbImportButton } from '../breadcrumbs/buttons/BreadCrumbImportButton';

const formatMetaDate = (ingestDate) => {
    if (!ingestDate) return '—';
    const d = new Date(ingestDate);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.getMonth() + 1}.${d.getDate()}.${d.getFullYear()}`;
};

export function LeaderBoard() {
    const dispatch = useDispatch();
    const listItems = useSelector((state) => state?.leaderboard?.listItems);
    const nextCursor = useSelector((state) => state?.leaderboard?.nextCursor);
    const isFetching = useSelector((state) => state?.leaderboard?.isFetching);
    const loadingState = useSelector((state) => state?.leaderboard?.loadingState);
    const searchFilters = useSelector((state) => state?.leaderboard?.filters);
    const sortBy = useSelector((state) => state?.leaderboard?.sortBy);
    const preventNextLoadMore = useSelector((state) => state?.app?.preventNextLoadMore);
    const previewIsShowing = useSelector((state) => state.preview.isShowing);
    const isImporting = useSelector((state) => state.import.isImporting);
    const deck = useSelector((state) => state.preview.previewDeck);
    const selectedListKey = useSelector((state) => state.leaderboard.selectedListKey);
    const selectedListIndex = useSelector((state) => state.leaderboard.selectedListIndex);

    const deckCount = useSelector((state) => state?.app?.stats?.totalCount | 0);
    const metaIngestDate = useSelector((state) => state?.app?.stats?.metaIndexDate);

    document.getElementById('body').className = previewIsShowing ? 'bodyNoScroll' : 'body';

    const handleSortChange = (sortIndex) => {
        dispatch(setLeaderboardSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchAll(null, searchFilters, true, SORT_MAP[sortIndex], sortDirection));
    };

    const sortDirection = useSelector((state) => state.leaderboard.sortDirection);
    const handleSortDirectionChange = (direction) => {
        dispatch(setLeaderboardSortDirection(direction));
        dispatch(fetchAll(null, searchFilters, true, sortBy, direction));
    };

    const handleFiltersChange = (filters) => {
        dispatch(leaderboardFetchFiltered(filters, sortBy, sortDirection));
    };

    const handleSelectionChange = (evn) => {
        dispatch(setSelectedListKey(evn));
    };

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const handleLoadMore = () => {
        if (preventNextLoadMore) {
            dispatch(resetPreventNextLoadMore());
            return;
        }

        if (!isFetching && nextCursor !== null) {
            dispatch(fetchAll(nextCursor, searchFilters, false, sortBy, sortDirection));
        }
    };

    const handler = () => {
        if (document.getElementById('resultsContainer')) {
            if (document.getElementById('resultsContainer')?.getBoundingClientRect()?.y <= 75) {
                document.getElementById('defaultImportButton').style.display = 'block';
            } else {
                document.getElementById('defaultImportButton').style.display = 'none';
            }
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handler, { passive: true });

        return () => {
            window.removeEventListener('scroll', handler);
        };
    }, []);

    let helmetName = `Leaderboard`;
    let helmetCanonical = `https://www.commandersalt.com`;
    let helmetDescription = `The saltiest MtG decks on the planet!`;
    let helmetTitle = `Commandersalt.com - The saltiest MtG decks on the planet!`;

    // TODO: remove this when details moved out of popup
    if (previewIsShowing) {
        helmetName = `Details ${deck?.title ? '> ' + deck.title : ''}`;
        helmetCanonical = `https://www.commandersalt.com`;
        helmetDescription = `MtG Commander deck ${deck?.author?.username ? 'by ' + deck?.author?.username : ''}`;
        helmetTitle = `Commandersalt.com - MtG Commander deck ${
            deck?.author?.username ? 'by ' + deck?.author?.username : ''
        }`;
    }

    const handleAlgorithmClick = () => {
        logEvent(getAnalytics(), 'leaderboard_algorithm', { url: deck?.deckUri });
        dispatch(setAppRoute({ route: `/algorithm` }));
    };

    const totalDecksLabel = deckCount > 0 ? deckCount.toLocaleString() : '—';
    const metaDateLabel = formatMetaDate(metaIngestDate);

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs
                content={
                    <Flex direction="row" gap="size-100" justifyContent="right">
                        <div id={'defaultImportButton'} style={{ display: 'none' }}>
                            <BreadCrumbImportButton isDisabled={isImporting} />
                        </div>
                    </Flex>
                }
            />
            <div className="heroPanelWrap">
                <div className="heroPanelCard panelGradient leaderboardHeroCard">
                    <div className="leaderboardHeroInfo">
                        <span className="heroPanelEyebrow">The saltiest commander decks on the planet</span>
                        <h1 className="leaderboardHeroTitle">Analyse your deck</h1>
                        <p className="leaderboardHeroTagline">
                            Paste a Commander decklist and get a seasoned report card — bracket, power level, saltiness
                            grade, win conditions, and the cards most likely to make your play group hate you.
                        </p>
                        <div className="leaderboardHeroSubmit">
                            <SubmitForm />
                        </div>
                        <button type="button" className="leaderboardHeroAlgorithmLink" onClick={handleAlgorithmClick}>
                            <span>How are power level scores determined?</span>
                            <ChevronRight size="XS" />
                        </button>
                    </div>
                    <div className="leaderboardHeroStatsGrid">
                        <div className="leaderboardHeroStat">
                            <span className="leaderboardHeroStatLabel">Decks indexed</span>
                            <span className="leaderboardHeroStatValue">{totalDecksLabel}</span>
                            <span className="leaderboardHeroStatHint">tracked and seasoned</span>
                        </div>
                        <div className="leaderboardHeroStat">
                            <span className="leaderboardHeroStatLabel">Meta refresh</span>
                            <span className="leaderboardHeroStatValue">{metaDateLabel}</span>
                            <span className="leaderboardHeroStatHint">last ingest</span>
                        </div>
                        <div className="leaderboardHeroStat leaderboardHeroStat--tag">
                            <span className="leaderboardHeroStatLabel">Status</span>
                            <span className="leaderboardHeroStatValue leaderboardHeroStatValue--tag">
                                <span className="leaderboardHeroStatusDot" aria-hidden="true" />
                                Extra Salty
                            </span>
                            <span className="leaderboardHeroStatHint">live — new decks flowing in</span>
                        </div>
                    </div>
                </div>
            </div>
            <ResultsListing
                id={'resultsContainer'}
                outerMarginTop={10}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                handleSortDirectionChange={handleSortDirectionChange}
                handleSelectionChange={handleSelectionChange}
                handleIndexSelection={handleIndexSelection}
                selectedListIndex={selectedListIndex}
                selectedListIndexPrefix={'leaderboard_'}
                selectedListKey={selectedListKey}
                routePath={`/`}
                allowDeckPreview={true}
                showFilters={false}
                hasMore={nextCursor !== null}
                columns={resultsColumns.LEADERBOARD()}
                columnRenderer={resultsColumnRenderers.LEADERBOARD}
                listItems={listItems}
                cellRenderer={resultsCellRenderers.LEADERBOARD}
                previewIsShowing={previewIsShowing}
                handleLoadMore={handleLoadMore}
                loadingState={loadingState}
                showFilterPanel={true}
                onFiltersChange={handleFiltersChange}
                filters={searchFilters}
                hideQueryInput={true}
            />
        </Flex>
    );
}
